import type { DirectorCatalog, DirectorPlan } from '@/features/director/contracts'

export const DIRECTOR_ROUTE_PREFIX = '/media-agent'
export type { DirectorCatalog, DirectorPlan } from '@/features/director/contracts'

export type SendChatOptions = {
  baseUrl?: string
  conversationId?: string | null
  fileUrls?: string[]
  userId?: string
  authToken?: string
  routePrefix?: string
  viralBoost?: boolean
  launchPlanId?: string | null
  currency?: string
}

export type PollOptions = {
  baseUrl?: string
  intervalMs?: number
  maxTicks?: number
  authToken?: string
  onTick?: (data: unknown) => void
  routePrefix?: string
  viralBoost?: boolean
  shouldStop?: () => boolean
}

export type DirectorFeedbackOptions = {
  baseUrl?: string
  authToken?: string
  routePrefix?: string
  conversationId?: string | null
  jobId?: string | null
  sessionId?: string
  rating: number
  review: string
}

export type DirectorCatalogOptions = {
  baseUrl?: string
  authToken?: string
  routePrefix?: string
}

export type ChatStartResponse =
  | { status: 'succeeded'; message: string; raw?: unknown; conversation_id?: string | null; job_ids?: string[] }
  | { status: 'running'; workflow_run_id: string; message?: string; raw?: unknown; conversation_id?: string | null; job_ids?: string[] }

export type PollChatResponse =
  | { status: 'succeeded'; message: string; raw?: unknown; job_ids?: string[] }
  | { status: 'failed'; error: string; details?: unknown; job_ids?: string[] }

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function getPreferredLanguage(): string {
  try {
    if (typeof window !== 'undefined') {
      const stored = window.localStorage.getItem('i18nextLng')
      if (typeof stored === 'string' && stored.trim()) return stored.trim()
    }
  } catch (error) {
    void error
  }
  if (typeof document !== 'undefined' && typeof document.documentElement?.lang === 'string' && document.documentElement.lang.trim()) {
    return document.documentElement.lang.trim()
  }
  if (typeof navigator !== 'undefined' && typeof navigator.language === 'string' && navigator.language.trim()) {
    return navigator.language.trim()
  }
  return 'en'
}

export async function sendChatMessage(text: string, opts: SendChatOptions = {}): Promise<ChatStartResponse> {
  const base = (opts.baseUrl || '').replace(/\/$/, '')
  const prefix = typeof opts.routePrefix === 'string' && opts.routePrefix.trim() ? opts.routePrefix.replace(/\/$/, '') : DIRECTOR_ROUTE_PREFIX
  const url = `${base}${prefix}/chat?mode=polling`
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (opts.authToken) headers['Authorization'] = `Bearer ${opts.authToken}`

  const body = {
    message: text,
    conversation_id: opts.conversationId ?? null,
    file_urls: Array.isArray(opts.fileUrls) ? opts.fileUrls : [],
    user_id: opts.userId || 'anonymous',
    viral_boost: Boolean(opts.viralBoost),
    language: getPreferredLanguage(),
    launch_plan_id: opts.launchPlanId ?? null,
    currency: typeof opts.currency === 'string' && opts.currency.trim() ? opts.currency.trim().toUpperCase() : undefined
  }

  // Cold-start aware fetch: long timeout, single retry on network/5xx errors
  // (Render free-tier dynos can take 30-50s to wake; a 502 may bounce back fast)
  const attemptFetch = async (timeoutMs: number): Promise<Response> => {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
    try {
      return await fetch(url, { method: 'POST', headers, body: JSON.stringify(body), signal: controller.signal })
    } finally {
      clearTimeout(timeoutId)
    }
  }

  const isColdStartError = (err: unknown): boolean => {
    const msg = err instanceof Error ? err.message : String(err || '')
    return /Failed to fetch|NetworkError|aborted|timeout|ECONN|fetch failed/i.test(msg)
  }

  let res: Response
  try {
    res = await attemptFetch(60000)
  } catch (firstError) {
    if (!isColdStartError(firstError)) throw firstError
    // Try a wake-up ping then retry once with a longer timeout
    try {
      const healthUrl = `${base}/health`
      await fetch(healthUrl, { method: 'GET', cache: 'no-store' }).catch(() => undefined)
    } catch (pingError) {
      void pingError
    }
    res = await attemptFetch(90000)
  }

  // Retry once on cold-start 5xx
  if (res.status === 502 || res.status === 503 || res.status === 504) {
    try {
      const healthUrl = `${base}/health`
      await fetch(healthUrl, { method: 'GET', cache: 'no-store' }).catch(() => undefined)
    } catch (pingError) {
      void pingError
    }
    await new Promise((r) => setTimeout(r, 1500))
    res = await attemptFetch(90000)
  }

  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const msg = res.status === 404
      ? `Director route not found. The app tried ${prefix}/chat, but the server does not expose that endpoint right now. Reload after deploy or restart the backend.`
      : (data && (data.message || data.error)) || `Error ${res.status}: ${res.statusText}`
    throw new Error(typeof msg === 'string' ? msg : 'Chat request failed')
  }
  if (!isRecord(data)) {
    throw new Error('Chat request failed')
  }
  const workflowRunId = isNonEmptyString(data.workflow_run_id) ? data.workflow_run_id.trim() : null
  const message = isNonEmptyString(data.message) ? data.message.trim() : null
  const jobIds = Array.isArray(data.job_ids)
    ? data.job_ids.filter((value): value is string => isNonEmptyString(value)).map((value) => value.trim())
    : []
  const conversationId: string | null =
    typeof data.conversation_id === 'string'
      ? data.conversation_id
      : data.conversation_id === null
        ? null
        : null

  if (workflowRunId) {
    return { status: 'running', workflow_run_id: workflowRunId, message: message ?? undefined, raw: data.raw ?? data, conversation_id: conversationId, job_ids: jobIds }
  }
  if (message) {
    return { status: 'succeeded', message, raw: data.raw ?? data, conversation_id: conversationId, job_ids: jobIds }
  }
  throw new Error('Chat request failed')
}

export async function pollChatStatus(
  runId: string,
  opts: PollOptions = {}
): Promise<PollChatResponse> {
  const base = (opts.baseUrl || '').replace(/\/$/, '')
  const intervalMs = Math.max(1000, opts.intervalMs ?? 3000)
  const maxTicks = Math.max(1, opts.maxTicks ?? 200)
  const headers: Record<string, string> = {}
  if (opts.authToken) headers['Authorization'] = `Bearer ${opts.authToken}`
  for (let i = 0; i < maxTicks; i++) {
    if (typeof opts.shouldStop === 'function' && opts.shouldStop()) {
      return { status: 'failed', error: 'Polling aborted', details: { reason: 'aborted' }, job_ids: [runId] }
    }
    const prefix = typeof opts.routePrefix === 'string' && opts.routePrefix.trim() ? opts.routePrefix.replace(/\/$/, '') : DIRECTOR_ROUTE_PREFIX
    const boostParam = opts.viralBoost ? '&viral_boost=true' : ''
    const res = await fetch(`${base}${prefix}/chat/status?run_id=${encodeURIComponent(runId)}${boostParam}`, { headers })
    const data = await res.json().catch(() => ({}))
    if (typeof opts.onTick === 'function') {
      try {
        opts.onTick(data)
      } catch (e) {
        void e
      }
    }
    if (res.ok && data?.status === 'succeeded') {
      const msg = typeof data?.message === 'string' ? data.message : ''
      const raw = data?.raw ?? data
      const rawJobId = isRecord(raw) && isNonEmptyString(raw.id) ? raw.id.trim() : null
      return { status: 'succeeded', message: msg, raw, job_ids: rawJobId ? [rawJobId] : [runId] }
    }
    if (!res.ok || data?.error) {
      const errParts: string[] = []
      if (res.status === 404) {
        errParts.push(`Director status route not found. The app tried ${prefix}/chat/status, but the backend does not expose it right now. Reload after deploy or restart the backend.`)
      }
      if (typeof data?.error === 'string' && data.error.trim()) errParts.push(data.error.trim())
      if (typeof data?.message === 'string' && data.message.trim()) errParts.push(data.message.trim())
      const errName = errParts.length > 0 ? errParts.join(': ') : `Error ${res.status}`
      return { status: 'failed', error: errName, details: data, job_ids: [runId] }
    }
    if (typeof opts.shouldStop === 'function' && opts.shouldStop()) {
      return { status: 'failed', error: 'Polling aborted', details: { reason: 'aborted' }, job_ids: [runId] }
    }
    await new Promise((r) => setTimeout(r, intervalMs))
  }
  return {
    status: 'failed',
    error: 'Timeout',
    details: {
      message: 'Status polling timed out while the job may still be processing in the background.',
      code: 'status_poll_timeout',
    },
    job_ids: [runId]
  }
}

export async function submitDirectorFeedback(opts: DirectorFeedbackOptions): Promise<{
  message: string
  learning_note?: string | null
  tool_issue_detected?: boolean
  tool_issue_summary?: string | null
  fix_recommendation?: string | null
}> {
  const base = (opts.baseUrl || '').replace(/\/$/, '')
  const prefix = typeof opts.routePrefix === 'string' && opts.routePrefix.trim() ? opts.routePrefix.replace(/\/$/, '') : DIRECTOR_ROUTE_PREFIX
  const url = `${base}${prefix}/internal/director/feedback`
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (opts.authToken) headers['Authorization'] = `Bearer ${opts.authToken}`
  const res = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      conversation_id: opts.conversationId ?? null,
      job_id: opts.jobId ?? null,
      session_id: opts.sessionId ?? null,
      rating: opts.rating,
      review: opts.review
    })
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const msg = (data && ((isRecord(data.error) && data.error.message) || data.message || data.error)) || `Error ${res.status}`
    throw new Error(typeof msg === 'string' ? msg : 'Feedback request failed')
  }
  const payload = isRecord(data) && isRecord(data.data) ? data.data : {}
  return {
    message: isNonEmptyString(payload.message) ? payload.message.trim() : 'Thanks for the review.',
    learning_note: isNonEmptyString(payload.learning_note) ? payload.learning_note.trim() : null,
    tool_issue_detected: Boolean(payload.tool_issue_detected),
    tool_issue_summary: isNonEmptyString(payload.tool_issue_summary) ? payload.tool_issue_summary.trim() : null,
    fix_recommendation: isNonEmptyString(payload.fix_recommendation) ? payload.fix_recommendation.trim() : null
  }
}

export async function getDirectorCatalog(opts: DirectorCatalogOptions = {}): Promise<DirectorCatalog> {
  const base = (opts.baseUrl || '').replace(/\/$/, '')
  const prefix = typeof opts.routePrefix === 'string' && opts.routePrefix.trim() ? opts.routePrefix.replace(/\/$/, '') : DIRECTOR_ROUTE_PREFIX
  const headers: Record<string, string> = {}
  if (opts.authToken) headers.Authorization = `Bearer ${opts.authToken}`
  const res = await fetch(`${base}${prefix}/director/catalog`, { headers })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const msg = typeof data?.message === 'string' && data.message.trim() ? data.message.trim() : `Error ${res.status}`
    throw new Error(msg)
  }
  const payload = isRecord(data?.data) ? data.data : {}
  const tools = Array.isArray(payload.tools) ? payload.tools : []
  const pipelines = Array.isArray(payload.pipelines) ? payload.pipelines : []
  const metadata = isRecord(payload.metadata)
    ? {
        catalog_version: isNonEmptyString(payload.metadata.catalog_version) ? payload.metadata.catalog_version.trim() : 'unknown',
        last_synced_at: isNonEmptyString(payload.metadata.last_synced_at) ? payload.metadata.last_synced_at.trim() : '',
        source: isNonEmptyString(payload.metadata.source) ? payload.metadata.source.trim() : 'unknown',
        degraded_mode: Boolean(payload.metadata.degraded_mode),
        experimental_tools: Array.isArray(payload.metadata.experimental_tools)
          ? payload.metadata.experimental_tools.filter((value): value is string => isNonEmptyString(value)).map((value) => value.trim())
          : [],
        experimental_pipelines: Array.isArray(payload.metadata.experimental_pipelines)
          ? payload.metadata.experimental_pipelines.filter((value): value is string => isNonEmptyString(value)).map((value) => value.trim())
          : [],
      }
    : undefined
  return { tools, pipelines, metadata } as DirectorCatalog
}
